import {differenceInCalendarDays, differenceInMilliseconds, setHours, startOfHour} from 'date-fns';

import {BUSINESS_HOURS_COUNT, TEST_DRIVE_CALENDAR} from '../constants/TEST_DRIVE_CALENDAR';

type BarLayout = {
  leftPercent: number;
  widthPercent: number;
};

const PERCENT = 100;
const MS_PER_HOUR = 3_600_000;
/** A drive with no measurable duration still has to be clickable. */
const MIN_WIDTH_FRACTION = 0.002;

const getFractionOfDay = (value: Date, isHourSubdivided: boolean) => {
  if (!isHourSubdivided) {
    return 0;
  }

  const businessStart = startOfHour(setHours(value, TEST_DRIVE_CALENDAR.BUSINESS_START_HOUR));
  const hoursIntoWindow = differenceInMilliseconds(value, businessStart) / MS_PER_HOUR;

  return hoursIntoWindow / BUSINESS_HOURS_COUNT;
};

/**
 * Percentages of the full grid width. Callers pass an already clamped, midnight-split segment,
 * so `start` and `end` sit inside one day. Day offsets go through `differenceInCalendarDays`,
 * never a millisecond division: the latter loses an hour over a DST transition and lands two
 * dates on one column.
 */
export const getTestDriveBarLayout = (
  start: Date,
  end: Date,
  windowStart: Date,
  rangeDays: number
): BarLayout => {
  const isHourSubdivided = rangeDays === TEST_DRIVE_CALENDAR.RANGE_DAYS.WEEK;

  const startOffset =
    differenceInCalendarDays(start, windowStart) + getFractionOfDay(start, isHourSubdivided);
  const endOffset = isHourSubdivided
    ? differenceInCalendarDays(end, windowStart) + getFractionOfDay(end, isHourSubdivided)
    : differenceInCalendarDays(end, windowStart) + 1;

  const width = Math.max(endOffset - startOffset, MIN_WIDTH_FRACTION);

  return {
    leftPercent: (startOffset / rangeDays) * PERCENT,
    widthPercent: (width / rangeDays) * PERCENT,
  };
};
